import { useEffect, useCallback, useState } from "react";
import { PostsDetail } from "@/types/pocketbase-types";
// Plate.js imports
import { Plate, TPlateEditor } from "@udecode/plate/react";
import { Editor, EditorContainer } from "@/components/ui/editor";
import { TElement } from "@udecode/plate";

// Import components
import { EditorToolbar } from "@/components/editor/EditorToolbar";
import { useCreateEditor } from "@/components/editor/use-create-editor";
import { createElementsFromOutline } from "@/pages/articles/outline-to-elements";
import { serializeToHtml } from "@/components/editor/html-serialization/serialize-to-html";

interface PlateEditorProps {
  postDetail: PostsDetail;
  onArticleChange: (
    contentState: TElement[],
    forceSave: boolean,
    htmlContent?: string,
  ) => void;
  title?: string;
  keyword?: string;
}

// Main PlateEditor component
export const PlateEditor: React.FC<PlateEditorProps> = ({
  postDetail,
  title,
  keyword,
  onArticleChange,
}) => {
  const editor = useCreateEditor((postDetail.contentState as TElement[]) || []);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (editor) {
      if (postDetail.outline && !postDetail.contentState) {
        try {
          const elements = createElementsFromOutline(
            postDetail,
            editor as unknown as TPlateEditor,
          );

          editor.tf.setValue(elements);
        } catch (error) {
          console.error("Error processing article content:", error);
        }
      }
      setInitialLoad(false);
    }
  }, [editor, postDetail, title]);

  const handleContentChange = useCallback(
    async ({ value }: { value: TElement[] }) => {
      const shouldSaveContentState = initialLoad && !postDetail.contentState;
      const htmlContent = await serializeToHtml(value);
      onArticleChange(value, shouldSaveContentState, htmlContent);
    },
    [onArticleChange, initialLoad, postDetail.contentState],
  );

  return (
    <div className="border flex flex-col h-full">
      <Plate editor={editor} onChange={handleContentChange}>
        {/* Main editor toolbar */}
        <EditorToolbar />

        {/* Scrollable content area */}
        <div
          className="overflow-y-auto"
          style={{ height: "calc(100vh - 50px)" }}
        >
          <EditorContainer>
            <Editor
              placeholder="Start writing your article content here..."
              variant="default"
            />
            {keyword && (
              <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200 text-sm text-yellow-800">
                <span className="font-medium">Target keyword:</span> {keyword}
              </div>
            )}
          </EditorContainer>
        </div>
      </Plate>
    </div>
  );
};
